/**
 * Meetings list page
 */

import {useState} from 'react';
import {useMeetings} from '../hooks/useMeetings';
import {useRealtimeMeetings} from '../hooks/useRealtime';
import {useMeetingsStore} from '@voxapilot/shared';
import {MeetingCard} from '../components/meetings/MeetingCard';
import {MeetingForm} from '../components/meetings/MeetingForm';
import {MeetingFilters} from '../components/meetings/MeetingFilters';
import {Pagination} from '../components/meetings/Pagination';
import {Modal} from '../components/ui/Modal';
import {Button} from '../components/ui/Button';
import {Loading} from '../components/ui/Loading';
import {EmptyState} from '../components/ui/EmptyState';
import type {CreateMeetingRequest, UpdateMeetingRequest} from '@voxapilot/shared';

export function MeetingsPage() {
  // Enable real-time updates
  useRealtimeMeetings();

  const {
    meetings,
    isLoading,
    isCreating,
    createMeeting,
  } = useMeetings();

  const {total, hasMore} = useMeetingsStore();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreateMeeting = async (data: CreateMeetingRequest) => {
    await createMeeting(data);
    setIsCreateModalOpen(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Meetings
        </h1>
        <Button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto">
          New Meeting
        </Button>
      </div>

      {/* Filters */}
      <MeetingFilters />

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loading size="lg" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <EmptyState
            icon="📝"
            title="No meetings yet"
            description="Create your first meeting to get started! Record, transcribe, and summarize your meetings with AI."
            action={{
              label: 'Create Meeting',
              onClick: () => setIsCreateModalOpen(true),
            }}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {meetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>
          
          {/* Pagination */}
          <Pagination total={total || meetings.length} hasMore={hasMore || false} />
        </>
      )}

      <Modal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        title="Create New Meeting"
        size="lg"
      >
        <MeetingForm
          onSubmit={handleCreateMeeting as (data: CreateMeetingRequest | UpdateMeetingRequest) => Promise<void>}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </Modal>
    </div>
  );
}

